import { useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../config/firebase";

export const useChatMessages = (chatId) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    if (!chatId) return;

    // First, try to load messages from localStorage for immediate display
    try {
      const chatMessagesKey = `chatMessages_${chatId}`;
      const storedMessages = JSON.parse(localStorage.getItem(chatMessagesKey) || '[]');
      
      if (storedMessages.length > 0) {
        console.log('📱 Loading messages from localStorage:', storedMessages.length);
        setMessages(storedMessages);
        setLoading(false);
      }
    } catch (error) {
      console.error('⚠️ Could not load messages from localStorage:', error);
    }

    // Check if we're online before attempting Firestore connection
    if (!navigator.onLine) {
      console.log('📱 Offline mode - using localStorage messages only');
      setIsOffline(true);
      setLoading(false);
      return;
    }

    const ref = collection(db, "chats", chatId, "messages");

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const msgs = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        const sortedMsgs = msgs.sort((a, b) => {
          // Handle Firebase Timestamps for sorting
          const getTimestamp = (msg) => {
            if (msg.createdAt && typeof msg.createdAt === 'object') {
              if (msg.createdAt.seconds) {
                return msg.createdAt.seconds * 1000; // Convert to milliseconds
              } else if (msg.createdAt.toDate) {
                return msg.createdAt.toDate().getTime();
              }
            }
            // Fallback to regular Date
            return new Date(msg.createdAt || msg.timestamp || 0).getTime();
          };
          
          return getTimestamp(a) - getTimestamp(b);
        });
        
        setMessages(sortedMsgs);
        setLoading(false);
        setIsOffline(false);
        
        // Save messages to localStorage for offline access
        try {
          const chatMessagesKey = `chatMessages_${chatId}`;
          localStorage.setItem(chatMessagesKey, JSON.stringify(sortedMsgs));
          console.log('💾 Saved messages to localStorage for offline access');
        } catch (error) {
          console.error('⚠️ Could not save messages to localStorage:', error);
        }
      },
      (err) => {
        console.error('❌ Firestore error:', err);
        setError(err.message);
        setLoading(false);
        
        // If Firestore fails, try to use localStorage as fallback
        try {
          const chatMessagesKey = `chatMessages_${chatId}`;
          const storedMessages = JSON.parse(localStorage.getItem(chatMessagesKey) || '[]');
          
          if (storedMessages.length > 0) {
            console.log('📱 Using localStorage messages as fallback:', storedMessages.length);
            setMessages(storedMessages);
            setError(null); // Clear error if we have fallback data
          }
        } catch (fallbackError) {
          console.error('❌ Fallback also failed:', fallbackError);
        }
      }
    );

    return () => unsub();
  }, [chatId]);

  // Listen for online/offline status changes
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 App came online, attempting to reconnect to Firestore...');
      setIsOffline(false);
      // The onSnapshot will automatically reconnect
    };
    
    const handleOffline = () => {
      console.log('📱 App went offline, continuing with localStorage data');
      setIsOffline(true);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { messages, loading, error, isOffline };
};
